import { Link } from "react-router-dom";
import { ArrowRight, Globe2, Disc3, HeartHandshake } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SocialIcons } from "@/components/SocialIcons";

export default function Discover() {
  return (
    <div className="pt-28 pb-24" data-testid="discover-page">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="eyebrow mb-3">The Label, In Full</p>
          <h1 className="font-serif text-3xl sm:text-5xl text-slate-50 leading-tight">
            Discover <span className="text-gold-gradient">Open Horizon Records</span>
          </h1>
          <p className="mt-7 text-slate-300/90 font-light leading-relaxed">
            Open Horizon Records is an independent music label dedicated to creating, producing,
            releasing and distributing original music. With the guiding philosophy "Music Beyond the
            Horizon", the label creates songs inspired by real emotions, personal experiences,
            memorable moments and the stories that connect people.
          </p>
          <p className="mt-5 text-slate-300/90 font-light leading-relaxed">
            The catalogue includes original singles, album releases, uplifting songs, emotional
            ballads, contemporary pop-inspired productions, reflective music and storytelling through
            song — including the nine-track album <em>Every Season Leaves a Mark</em> alongside
            standalone singles such as <em>Chasing Joy</em>, <em>Dancing in the Rain</em>,{" "}
            <em>Let the Leaves Fall</em>, <em>Letting Go</em> and <em>The Best Is Yet to Come</em>.
          </p>
        </Reveal>

        <Reveal className="mt-14">
          <div className="rounded-2xl border border-white/10 bg-navy-900/60 p-8 sm:p-10">
            <div className="flex items-center gap-3 mb-4">
              <Globe2 size={18} className="text-gold-400" />
              <h2 className="font-serif text-xl sm:text-2xl text-slate-50">Global Music Distribution</h2>
            </div>
            <p className="text-sm text-slate-300/90 font-light leading-relaxed">
              Open Horizon Records releases original music internationally through DistroKid, making
              recordings accessible through major digital music platforms and streaming services,
              including Spotify, Apple Music, Amazon Music, YouTube Music, Deezer, TIDAL and other
              participating services. Listeners can discover Open Horizon Records music through
              streaming platforms, digital stores and selected social media music libraries, subject
              to each release's availability.
            </p>
          </div>
        </Reveal>

        <Reveal className="mt-8">
          <div className="rounded-2xl border border-white/10 bg-navy-900/60 p-8 sm:p-10">
            <div className="flex items-center gap-3 mb-4">
              <HeartHandshake size={18} className="text-gold-400" />
              <h2 className="font-serif text-xl sm:text-2xl text-slate-50">Personalised Songs &amp; Bespoke Music Creation</h2>
            </div>
            <p className="text-sm text-slate-300/90 font-light leading-relaxed">
              Your Story. Your Song. Created With You. Open Horizon Records offers a bespoke
              songwriting and music production service for people who want to turn meaningful
              memories, personal stories and special occasions into original songs. The process begins
              with a personal consultation and a detailed Song Story Questionnaire, covering names,
              memories, emotions, musical preferences and the details that make each story unique.
              Original lyrics and musical interpretations are then developed into a professionally
              produced personalised song.
            </p>
            <p className="text-sm text-slate-300/90 font-light leading-relaxed mt-4">
              Suitable for personalised wedding songs and anniversary gifts, original birthday songs
              and milestone celebrations, romantic songs, family tributes and friendship celebrations,
              memorial songs and remembrance music, retirement songs, songs for new beginnings and
              other meaningful occasions.
            </p>
            <Link to="/bespoke" className="btn-gold mt-7 !px-6 !py-2.5 text-xs" data-testid="discover-bespoke-cta">
              Explore Bespoke Music <ArrowRight size={14} />
            </Link>
          </div>
        </Reveal>

        <Reveal className="mt-8">
          <div className="rounded-2xl border border-white/10 bg-navy-900/60 p-8 sm:p-10">
            <div className="flex items-center gap-3 mb-4">
              <Disc3 size={18} className="text-gold-400" />
              <h2 className="font-serif text-xl sm:text-2xl text-slate-50">The Catalogue</h2>
            </div>
            <p className="text-sm text-slate-300/90 font-light leading-relaxed">
              Browse every release with 60-second previews, full lyrics and the story behind each
              song — then continue listening on Spotify, Apple Music and other major services.
            </p>
            <Link to="/music" className="btn-ghost-gold mt-7 !px-6 !py-2.5 text-xs" data-testid="discover-music-cta">
              Explore Our Music <ArrowRight size={14} />
            </Link>
          </div>
        </Reveal>

        <Reveal className="mt-12 text-center">
          <p className="text-xs tracking-[0.25em] uppercase text-slate-500 mb-4">Follow the horizon</p>
          <SocialIcons className="justify-center" size={20} />
          <p className="mt-6 text-xs text-slate-500">openhorizonrecords@outlook.com</p>
        </Reveal>
      </div>
    </div>
  );
}
